import { ScheduleRowEntity } from '../../../apis/schedule/schedule-row-entity';
import { TeamSettingEntity } from '../../../apis/team/team-setting-entity';
import { ScheduleViewModel } from '../../../components/schedule/schedule-view-model';
import { setTeamHighlight } from '../../../components/schedule/team-highlight';

export class TeamGame {
    public game: ScheduleRowEntity = null;
    public fields: number = 0;
    public homeTeamExists: boolean = false;
    public opponentTeamExists: boolean = false;
    public onDropCallback: (dropData: TeamDropInfo) => void;
    private _teams: TeamSettingEntity[] = null;

    constructor(game: ScheduleRowEntity, fields: number, onDropCallback: (dropData: TeamDropInfo) => void, teams: TeamSettingEntity[]) {
        this.game = game;
        this.fields = fields;
        this.onDropCallback = onDropCallback;
        this._teams = teams;
        this.homeTeamExists = teams.any(t => t.name === game.homeTeam);
        this.opponentTeamExists = teams.any(t => t.name === game.opponentTeam);
    }

    public get homeTeamIsEmpty(): boolean {
        return this.game.homeTeam === ScheduleViewModel.freeSlotTeamName;
    }
    public get opponentTeamIsEmpty(): boolean {
        return this.game.opponentTeam === ScheduleViewModel.freeSlotTeamName;
    }
    public get homeTeam(): string {
        return this.game.homeTeam;
    }

    public set homeTeam(value: string) {
        this.game.homeTeam = value;
        this.homeTeamExists = this._teams.any(t => t.name === this.game.homeTeam);
    }
    public get opponentTeam(): string {
        return this.game.opponentTeam;
    }
    public set opponentTeam(value: string) {
        this.game.opponentTeam = value;
        this.opponentTeamExists = this._teams.any(t => t.name === this.game.opponentTeam);
    }

    public get fieldId(): number {
        return this.game.fieldId;
    }

    public set fieldId(value: number) {
        this.game.fieldId = value;
    }

    public get venueKey(): string {
        return this.game.fieldId?.toString() ?? '';
    }

    public get interval(): number {
        return this.game.interval;
    }

    public get scheduleRow(): ScheduleRowEntity {
        return this.game;
    }

    public get groupId(): string {
        return this.game.groupId;
    }

    public get isEmpty(): boolean {
        return this.homeTeam === ScheduleRowEntity.freeSlotTeamName && this.opponentTeam === ScheduleRowEntity.freeSlotTeamName;
    }
    public get isValid(): boolean {
        return this.homeTeam !== ScheduleRowEntity.freeSlotTeamName && this.opponentTeam !== ScheduleRowEntity.freeSlotTeamName;
    }

    public mouseOver(isHomeTeam: boolean): void {
        setTeamHighlight(isHomeTeam ? this.homeTeam : this.opponentTeam, true);
    }
    public mouseLeave(isHomeTeam: string): void {
        setTeamHighlight(isHomeTeam ? this.homeTeam : this.opponentTeam, false);
    }

    public allow(ev: DragEvent): void {
        ev.preventDefault();
    }

    public allowDrop(ev: DragEvent): void {
        const sourceGroupId = ev.dataTransfer.getData('groupId');
        if (sourceGroupId && this.groupId && sourceGroupId !== this.groupId) {
            ev.dataTransfer.dropEffect = 'none';
            return;
        }
        ev.preventDefault();
        ev.dataTransfer.dropEffect = 'move';
    }
    public dragHomeTeam(ev: DragEvent): boolean {
        ev.dataTransfer.setData('team', this.homeTeam);
        ev.dataTransfer.setData('type', TeamDropInfoType.homeTeam.toString());
        ev.dataTransfer.setData('interval', this.game.interval.toString());
        ev.dataTransfer.setData('venueKey', this.game.fieldId?.toString() ?? '');
        ev.dataTransfer.setData('groupId', this.groupId ?? '');
        return true;
    }
    public dragOpponentTeam(ev: DragEvent): boolean {
        ev.dataTransfer.setData('team', this.opponentTeam);
        ev.dataTransfer.setData('type', TeamDropInfoType.opponentTeam.toString());
        ev.dataTransfer.setData('interval', this.game.interval.toString());
        ev.dataTransfer.setData('venueKey', this.game.fieldId?.toString() ?? '');
        ev.dataTransfer.setData('groupId', this.groupId ?? '');
        return true;
    }

    public dropHomeTeam(ev: DragEvent): void {
        ev.preventDefault();
        const sourceTeam = ev.dataTransfer.getData('team');
        const sourceTeamType = parseInt(ev.dataTransfer.getData('type'), 10) as TeamDropInfoType;
        const sourceInterval = parseInt(ev.dataTransfer.getData('interval'), 10);
        const venueKey = ev.dataTransfer.getData('venueKey');
        const dropData = new TeamDropInfo(sourceTeam, sourceTeamType, sourceInterval, venueKey, this.homeTeam, TeamDropInfoType.homeTeam, this.game.interval, this.game.fieldId?.toString() ?? '');
        this.onDropCallback(dropData);

    }

    public dropOpponentTeam(ev: DragEvent): void {
        ev.preventDefault();
        const sourceTeam = ev.dataTransfer.getData('team');
        const sourceTeamType = parseInt(ev.dataTransfer.getData('type'), 10) as TeamDropInfoType;
        const sourceInterval = parseInt(ev.dataTransfer.getData('interval'), 10);
        const venueKey = ev.dataTransfer.getData('venueKey');
        const dropData = new TeamDropInfo(sourceTeam, sourceTeamType, sourceInterval, venueKey, this.opponentTeam, TeamDropInfoType.opponentTeam, this.game.interval, this.game.fieldId?.toString() ?? '');
        this.onDropCallback(dropData);
    }

}

export class TeamDropInfo {
    public sourceTeam: string;
    public sourceTeamType: TeamDropInfoType = null;
    public sourceInterval: number;
    public sourceVenueKey: string;
    public targetTeam: string;
    public targetTeamType: TeamDropInfoType = null;
    public targetInterval: number;
    public targetVenueKey: string;

    constructor(sourceTeam: string, sourceTeamType: TeamDropInfoType, sourceInterval: number, sourceVenueKey: string, targetTeam: string, targetTeamType: TeamDropInfoType, targetInterval: number, targetVenueKey: string) {
        this.sourceTeam = sourceTeam;
        this.sourceTeamType = sourceTeamType;
        this.sourceInterval = sourceInterval;
        this.sourceVenueKey = sourceVenueKey;
        this.targetTeam = targetTeam;
        this.targetTeamType = targetTeamType;
        this.targetInterval = targetInterval;
        this.targetVenueKey = targetVenueKey;
    }

    public toString(): string {
        return `${this.sourceTeam},${this.sourceInterval},${this.sourceVenueKey},${this.targetTeam},${this.targetInterval},${this.targetVenueKey}`;
    }
}

// eslint-disable-next-line no-shadow
export enum TeamDropInfoType {
    homeTeam = 1,
    opponentTeam = 2,
    removedTeam = 3

}
